import { useShortcutActions } from 'app/_hooks'
import Hls from 'hls.js'
import Image from 'next/image'
import { useCallback, useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import { useFullscreen } from '../useFullscreen'
import { useLogger } from '../useLogger'
import { useVideoScale } from '../useVideoScale'
import { Menu } from './Menu'
import { PlayerButton } from './PlayerButton'
import { SeekBar } from './SeekBar'
import { Volume } from './Volume'

const IconSizeBase = 64

type Props = {
  title: string
  video: HTMLVideoElement
  hls: Hls | null
  showedControls: boolean
  isPlaying: boolean
  currentTime: number
  duration: number
  vtts: {
    default: boolean
    url: string
    locale: string
    label: string
  }[]
  flashIcon: (type: string) => void
}

export const Controller = ({
  title,
  video,
  hls,
  showedControls,
  isPlaying,
  currentTime,
  duration,
  vtts,
  flashIcon,
}: Props) => {
  const { fullscreen, setFullscreen } = useFullscreen()
  const { forward: forwardLog, rewind: rewindLog, play: playLog, pause: pauseLog } = useLogger()
  const { videoScale } = useVideoScale()

  const handleRewind = useCallback(() => {
    video.currentTime = Math.max(video.currentTime - 10, 0)
    rewindLog()
  }, [video, rewindLog])

  const handleForward = useCallback(() => {
    video.currentTime = Math.min(video.currentTime + 10, video.duration)
    forwardLog()
  }, [video, forwardLog])

  const handlePlay = useCallback(() => {
    video.play()
    playLog()
  }, [video, playLog])

  const handlePause = useCallback(() => {
    video.pause()
    pauseLog()
  }, [video, pauseLog])

  const shortcutActions = useMemo(
    () => ({
      Space: () => {
        if (video.paused) {
          handlePlay()
        } else {
          handlePause()
        }
      },
      ArrowRight: () => {
        handleForward()
        flashIcon('forward_10')
      },
      ArrowLeft: () => {
        handleRewind()
        flashIcon('rewind_10')
      },
      KeyF: () => {
        setFullscreen(!fullscreen)
      },
    }),
    [
      video,
      handlePlay,
      handlePause,
      handleForward,
      handleRewind,
      flashIcon,
      fullscreen,
      setFullscreen,
    ],
  )
  const { isShortcutEnabled } = useShortcutActions(shortcutActions)
  useEffect(() => {
    if (!isShortcutEnabled('Space')) {
      handlePause()
    }
  }, [isShortcutEnabled, handlePause])

  const IconSize = useMemo(() => videoScale * IconSizeBase * (isMobile ? 1.5 : 1), [videoScale])

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.24)',
        opacity: showedControls ? 1 : 0,
        transition: showedControls
          ? 'all 0.2s cubic-bezier(0.3, 0.3, 0.3, 1)'
          : 'all 0.3s cubic-bezier(0, 0.5, 0.7, 0.7)',
      }}
    >
      <div
        onClick={() => (isPlaying ? handlePause : handlePlay)()}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      />
      {!isMobile && (
        <div
          style={{
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            padding: `${videoScale * 28}px ${videoScale * 16}px ${videoScale * 28}px ${
              videoScale * 32
            }px`,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              pointerEvents: 'auto',
            }}
          >
            <Volume video={video} flashIcon={flashIcon} />
          </div>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '100%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            pointerEvents: 'auto',
          }}
        >
          <PlayerButton onClick={handleRewind} padding={isMobile ? '16px' : '0'}>
            <Image src='/player/rewind_10.png' alt='Rewind' width={IconSize} height={IconSize} />
          </PlayerButton>
        </div>
        <div
          style={{
            pointerEvents: 'auto',
          }}
        >
          {isPlaying ? (
            <PlayerButton onClick={() => handlePause()}>
              <Image src='/player/pause.png' alt='Pause' width={IconSize} height={IconSize} />
            </PlayerButton>
          ) : (
            <PlayerButton onClick={() => handlePlay()}>
              <Image src='/player/play.png' alt='Play' width={IconSize} height={IconSize} />
            </PlayerButton>
          )}
        </div>
        <div
          style={{
            pointerEvents: 'auto',
          }}
        >
          <PlayerButton onClick={handleForward} padding={isMobile ? '16px' : '0'}>
            <Image src='/player/forward_10.png' alt='Forward' width={IconSize} height={IconSize} />
          </PlayerButton>
        </div>
      </div>
      <div
        style={{
          position: 'absolute',
          bottom: '0',
          left: '0',
          width: '100%',
          pointerEvents: 'none',
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <SeekBar video={video} currentTime={currentTime} duration={duration} />
        </div>
        <Menu video={video} hls={hls} vtts={vtts} />
      </div>
    </div>
  )
}
